import { useToast } from './use-toast';
import { useAuth } from '../contexts/AuthContext';
import { useGeneration } from '../contexts/GenerationContext';
import { apiUrl } from '../config/api';

/**
 * Shared generation-start logic for BookCard and BookDetailsModal.
 *
 * startGeneration(book):
 *   1. POST /api/books/:id/generate (confirmed=true)
 *   2. Handle error cases (budget, in-progress, storage) with toasts
 *   3. Register in GenerationContext for persistent polling
 *   4. POST /api/library/add (source: 'saved') so it appears in user's library
 *
 * Returns { start } — async function accepting a book.
 * Caller is responsible for updating local isSaved UI state via the returned
 * boolean (true = succeeded & added to library).
 */
export function useGenerateBook() {
  const { toast } = useToast();
  const { user, token } = useAuth();
  const { startGeneration: trackGeneration } = useGeneration();

  const start = async (book) => {
    if (!user) {
      toast({
        variant: "destructive",
        title: "Not logged in",
        description: "Please log in to add books to your library.",
      });
      return { ok: false };
    }

    try {
      // Start generation
      const generateResponse = await fetch(apiUrl(`/api/books/${book.id}/generate`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmed: true })
      });

      const generateData = await generateResponse.json();

      if (generateData.error === 'budget_exceeded') {
        toast({
          variant: "destructive",
          title: "Generation Limit Reached",
          description: "Maximum testing budget for books reached. Purchase an official license for full access.",
          duration: 10000,
        });
        return { ok: false, errorCode: 'budget_exceeded' };
      }

      if (generateData.error === 'generation_in_progress') {
        toast({
          variant: "destructive",
          title: "Another Book Generating",
          description: generateData.message || 'Please wait for the current generation to finish.',
          duration: 8000,
        });
        return { ok: false, errorCode: 'generation_in_progress' };
      }

      if (generateData.error === 'insufficient_storage') {
        toast({
          variant: "destructive",
          title: "Not Enough Storage",
          description: generateData.message || 'This book is too large for available storage.',
          duration: 10000,
        });
        return { ok: false, errorCode: 'insufficient_storage' };
      }

      if (!generateResponse.ok) {
        throw new Error(generateData.message || generateData.error || 'Generation request failed');
      }

      // Track in context (persists across navigation)
      trackGeneration(book.id);

      // Add to library
      await fetch(apiUrl('/api/library/add'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ book_id: book.id, source: 'saved' })
      });

      toast({
        title: "Generating audio...",
        description: "This book is being generated and added to your library.",
      });
      return { ok: true };
    } catch (error) {
      console.error('[useGenerateBook] Error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to start generation. Please try again.",
      });
      return { ok: false, errorCode: 'exception' };
    }
  };

  return { start };
}
